import React, { useState, useEffect, useCallback, useMemo } from 'react';
import LedgerPanel from './LedgerPanel';

interface BudgetVersion {
    id: number;
    fiscal_year: number;
    version_type: string;
    version_name: string;
    is_active: boolean;
}

// Hojo Level
interface BudgetHojoItem {
    id: number;
    code: string;
    name: string;
    budget: number;
    previous_budget: number;
    initial_amount: number;
    correction_amount: number;
    actual: number;
    diff: number;
}

// Kamoku Level
interface BudgetKamokuItem {
    id: number;
    code: string;
    name: string;
    budget: number;
    previous_budget: number;
    initial_amount: number;
    correction_amount: number;
    actual: number;
    diff: number;
    children: BudgetHojoItem[];
    is_input: boolean;
    /** 既存の中区分直下 (hojo_id=NULL) 予算がある（編集不可・表示のみ） */
    budget_at_kamoku_level?: boolean;
    /** 中区分に直接入っている額（合計に加算、入力はしない） */
    kamoku_level_amount?: number;
    kamoku_level_initial_amount?: number;
    kamoku_level_correction_amount?: number;
}

// Chukubun Level
interface BudgetChukubunItem {
    id: number;
    code: string;
    name: string;
    budget: number;
    previous_budget: number;
    initial_amount: number;
    correction_amount: number;
    actual: number;
    diff: number;
    children: BudgetKamokuItem[];
}

// Daikubun Level
interface BudgetDaikubunItem {
    id: number;
    code: string;
    name: string;
    shikin_syuushi_id: number;
    budget: number;
    previous_budget: number;
    initial_amount: number;
    correction_amount: number;
    actual: number;
    diff: number;
    children: BudgetChukubunItem[];
}

interface BudgetResponse {
    items: BudgetDaikubunItem[];
    carryover: number;
}

interface BudgetManagementProps {
    initialTab?: number;
    onBack?: () => void;
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
    tenantName: string;
    onNavigate?: (menu: string, params?: any) => void;
}

const BudgetManagement: React.FC<BudgetManagementProps> = ({
    initialTab = 0,
    apiFetch,
    currentTenantId,
}) => {
    // Data State
    const [budgets, setBudgets] = useState<BudgetDaikubunItem[]>([]);
    const [versions, setVersions] = useState<BudgetVersion[]>([]);
    const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
    const [carryoverAmount, setCarryoverAmount] = useState<number>(0);
    const [originalCarryover, setOriginalCarryover] = useState<number>(0); // For identifying changes

    // UI State
    const [currentTab, setCurrentTab] = useState(initialTab);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [fiscalYear, setFiscalYear] = useState(2025);
    const [showNewVersionModal, setShowNewVersionModal] = useState(false);
    const [downloadingMonthlyPdf, setDownloadingMonthlyPdf] = useState(false);

    // New Version Input
    const [newVersionType, setNewVersionType] = useState('initial');
    const [newVersionName, setNewVersionName] = useState('当初予算');

    // Ledger Panel State (Split View)
    const [ledgerState, setLedgerState] = useState<{
        isOpen: boolean;
        kamokuId: number;
        kamokuName: string;
        hojoId: number | null;
        hojoName: string | null;
    }>({
        isOpen: false,
        kamokuId: 0,
        kamokuName: '',
        hojoId: null,
        hojoName: null
    });

    // Totals State
    const [totals, setTotals] = useState({
        income: 0,
        expense: 0,
        balance: 0,
        finalBalance: 0 // including carryover
    });

    // Determines if supplemental (revision) mode
    const isRevision = useMemo(() => {
        if (!selectedVersionId || versions.length === 0) return false;
        const v = versions.find(v => v.id === selectedVersionId);
        return v ? v.version_type !== 'initial' : false;
    }, [selectedVersionId, versions]);

    const handleDownloadMonthlyPdf = async () => {
        if (!currentTenantId) {
            setError('テナントが選択されていません');
            return;
        }
        setDownloadingMonthlyPdf(true);
        setError(null);
        try {
            const response = await apiFetch(
                `/api/budgets/monthly/pdf?fiscal_year=${fiscalYear}`
            );
            if (!response.ok) {
                const detail = await response.text();
                throw new Error(detail || '月別予算執行PDFの取得に失敗しました');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `budget_monthly_${fiscalYear}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setSuccessMessage('月別予算執行PDFをダウンロードしました');
        } catch (err) {
            const message = err instanceof Error ? err.message : '月別予算執行PDFの取得に失敗しました';
            setError(message);
        } finally {
            setDownloadingMonthlyPdf(false);
        }
    };

    // -------------------------------------------------------------------------
    // Data Fetching
    // -------------------------------------------------------------------------

    const fetchVersions = useCallback(async () => {
        try {
            const response = await apiFetch(`/api/budgets/versions?fiscal_year=${fiscalYear}`);
            if (!response.ok) throw new Error('Failed to fetch versions');
            const data = await response.json();
            setVersions(data);
            if (data.length > 0 && !selectedVersionId) {
                const active = data.find((v: BudgetVersion) => v.is_active);
                setSelectedVersionId(active ? active.id : data[0].id);
            } else if (data.length === 0) {
                setSelectedVersionId(null);
            }
        } catch (err) {
            console.error('Failed to fetch versions:', err);
        }
    }, [apiFetch, fiscalYear, selectedVersionId]);

    const fetchBudgets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let url = `/api/budgets/v2?fiscal_year=${fiscalYear}`;
            if (selectedVersionId) {
                url += `&version_id=${selectedVersionId}`;
            }
            const response = await apiFetch(url);
            if (!response.ok) throw new Error('Failed to fetch budgets');

            // New V2 format returns generic dict or specific structure. 
            // We assume backend returns { items: [], carryover: 0 } now.
            const data: BudgetResponse = await response.json();

            // Handle both lists (old format backup) and new dict format
            if (Array.isArray(data)) {
                setBudgets(data);
                setCarryoverAmount(0);
                setOriginalCarryover(0);
            } else {
                setBudgets(data.items);
                setCarryoverAmount(data.carryover || 0);
                setOriginalCarryover(data.carryover || 0);
            }
        } catch (err) {
            setError('予算データの取得に失敗しました');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, fiscalYear, selectedVersionId]);

    useEffect(() => {
        if (currentTenantId) {
            fetchVersions();
        }
    }, [currentTenantId, fiscalYear, fetchVersions]);

    useEffect(() => {
        if (currentTenantId && selectedVersionId) {
            fetchBudgets();
        }
    }, [selectedVersionId, currentTenantId, fetchBudgets]);

    // Calculate totals whenever budgets or carryover change
    useEffect(() => {
        let income = 0;
        let expense = 0;

        budgets.forEach(d => {
            // Mapping: 1=Revenue, 2=Expense
            if (d.shikin_syuushi_id === 1) {
                income += d.budget;
            } else if (d.shikin_syuushi_id === 2) {
                expense += d.budget;
            }
        });

        const balance = income - expense;
        const finalBalance = balance + carryoverAmount;

        setTotals({
            income,
            expense,
            balance,
            finalBalance
        });
    }, [budgets, carryoverAmount]);

    // -------------------------------------------------------------------------
    // Handlers
    // -------------------------------------------------------------------------

    const handleBudgetChange = (
        daikubunIndex: number,
        chukubunIndex: number,
        kamokuIndex: number,
        hojoIndex: number | null,
        value: string,
        isAdjustment: boolean = false
    ) => {
        const inputVal = parseInt(value) || 0;
        const newBudgets = [...budgets];
        const daikubun = newBudgets[daikubunIndex];
        const chukubun = daikubun.children[chukubunIndex];
        const kamoku = chukubun.children[kamokuIndex];

        // 1. Update Target Item
        // 補正版の入力値は当該版の増減額（デルタ）。補正後予算 = (既存合計 − 旧デルタ) + 新デルタ。
        if (hojoIndex !== null) {
            // Hojo Update
            const targetHojo = kamoku.children[hojoIndex];
            if (isAdjustment) {
                const baseWithoutThisDelta = targetHojo.budget - targetHojo.correction_amount;
                targetHojo.correction_amount = inputVal;
                targetHojo.budget = baseWithoutThisDelta + inputVal;
            } else {
                targetHojo.budget = inputVal;
            }
            targetHojo.diff = targetHojo.budget - targetHojo.actual;
        } else {
            // Kamoku Update（小区分なしの科目のみ）
            if (!kamoku.is_input || kamoku.children.length > 0) return;

            if (isAdjustment) {
                const baseWithoutThisDelta = kamoku.budget - kamoku.correction_amount;
                kamoku.correction_amount = inputVal;
                kamoku.budget = baseWithoutThisDelta + inputVal;
            } else {
                kamoku.budget = inputVal;
            }
            kamoku.diff = kamoku.budget - kamoku.actual;
        }

        // 2. Recalculate Hierarchy
        // 小区分あり: 中区分行は小区分合計＋中区分直下額（編集不可）
        if (kamoku.children.length > 0) {
            const levelAmt = kamoku.kamoku_level_amount || 0;
            const levelInit = kamoku.kamoku_level_initial_amount || 0;
            const levelCorr = kamoku.kamoku_level_correction_amount || 0;
            kamoku.budget = kamoku.children.reduce((sum, h) => sum + h.budget, 0) + levelAmt;
            kamoku.initial_amount =
                kamoku.children.reduce((sum, h) => sum + h.initial_amount, 0) + levelInit;
            kamoku.correction_amount =
                kamoku.children.reduce((sum, h) => sum + h.correction_amount, 0) + levelCorr;
            kamoku.diff = kamoku.budget - kamoku.actual;
        }

        // Chukubun total（科目行は既に小区分＋直下を含む）
        chukubun.budget = chukubun.children.reduce((sum, k) => sum + k.budget, 0);
        chukubun.initial_amount = chukubun.children.reduce((sum, k) => sum + k.initial_amount, 0);
        chukubun.correction_amount = chukubun.children.reduce((sum, k) => sum + k.correction_amount, 0);
        chukubun.diff = chukubun.budget - chukubun.actual;

        // Daikubun total
        daikubun.budget = daikubun.children.reduce((sum, c) => sum + c.budget, 0);
        daikubun.initial_amount = daikubun.children.reduce((sum, c) => sum + c.initial_amount, 0);
        daikubun.correction_amount = daikubun.children.reduce((sum, c) => sum + c.correction_amount, 0);
        daikubun.diff = daikubun.budget - daikubun.actual;

        setBudgets(newBudgets);
    };

    const handleSaveBudgets = async () => {
        if (!selectedVersionId) {
            setError('予算バージョンを選択してください');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            // 1. Add Carryover Save if changed
            if (carryoverAmount !== originalCarryover) {
                await apiFetch(`/api/budgets/v2/${selectedVersionId}/carryover?amount=${carryoverAmount}`, {
                    method: 'PUT'
                });
                setOriginalCarryover(carryoverAmount);
            }

            // 2. Flatten budget items
            // 当初版: amount = 絶対額。補正版: amount = 増減額（デルタ）。
            const updateList: { kamoku_id: number; hojo_id: number | null; amount: number }[] = [];

            budgets.forEach(daikubun => {
                daikubun.children.forEach(chukubun => {
                    chukubun.children.forEach(kamoku => {
                        if (kamoku.children.length > 0) {
                            // 小区分あり: 小区分のみ保存（中区分直下は新規作成しない）
                            kamoku.children.forEach(hojo => {
                                updateList.push({
                                    kamoku_id: kamoku.id,
                                    hojo_id: hojo.id,
                                    amount: isRevision ? hojo.correction_amount : hojo.budget
                                });
                            });
                        } else if (kamoku.is_input) {
                            updateList.push({
                                kamoku_id: kamoku.id,
                                hojo_id: null,
                                amount: isRevision ? kamoku.correction_amount : kamoku.budget
                            });
                        }
                    });
                });
            });

            const response = await apiFetch(`/api/budgets/v2/${selectedVersionId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ budgets: updateList })
            });

            if (!response.ok) throw new Error('Failed to save budgets');
            setSuccessMessage('予算を保存しました');
        } catch (err) {
            setError('予算の保存に失敗しました');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateVersion = async () => {
        setLoading(true);
        try {
            const response = await apiFetch('/api/budgets/versions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fiscal_year: fiscalYear,
                    version_type: newVersionType,
                    version_name: newVersionName,
                })
            });
            if (!response.ok) throw new Error('Failed to create version');
            const newVersion = await response.json();
            setVersions([...versions, newVersion]);
            setSelectedVersionId(newVersion.id);
            setShowNewVersionModal(false);
            setSuccessMessage('予算バージョンを作成しました');
        } catch (err) {
            setError('予算バージョンの作成に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyFromPrevious = async (sourceVersionId: number) => {
        if (!selectedVersionId) return;
        if (!window.confirm('現在の内容が上書きされます。よろしいですか？')) return;

        setLoading(true);
        try {
            const response = await apiFetch(
                `/api/budgets/v2/${selectedVersionId}/copy-from/${sourceVersionId}`,
                { method: 'POST' }
            );
            if (!response.ok) throw new Error('Failed to copy');
            const result = await response.json();
            setSuccessMessage(result.message);
            fetchBudgets();
        } catch (err) {
            setError('予算のコピーに失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleFillFromActual = async () => {
        if (!selectedVersionId) return;
        if (!window.confirm('現在の内容が上書きされます。よろしいですか？')) return;

        setLoading(true);
        try {
            const response = await apiFetch(
                `/api/budgets/v2/${selectedVersionId}/fill-from-actual?fiscal_year=${fiscalYear}`,
                { method: 'POST' }
            );
            if (!response.ok) throw new Error('Failed to fill');
            const result = await response.json();
            setSuccessMessage(result.message);
            fetchBudgets();
        } catch (err) {
            setError('実績からの作成に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenLedger = (kamoku: { id: number; name: string }, hojo: { id: number; name: string } | null = null) => {
        // Toggle if same item clicked, otherwise open new
        if (ledgerState.isOpen && ledgerState.kamokuId === kamoku.id && ledgerState.hojoId === (hojo ? hojo.id : null)) {
            setLedgerState(prev => ({ ...prev, isOpen: false }));
        } else {
            setLedgerState({
                isOpen: true,
                kamokuId: kamoku.id,
                kamokuName: kamoku.name,
                hojoId: hojo ? hojo.id : null,
                hojoName: hojo ? hojo.name : null
            });
        }
    };

    // -------------------------------------------------------------------------
    // Render Helper
    // -------------------------------------------------------------------------

    // Formatting
    const formatCurrency = (val: number) => new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(val);
    const formatNumber = (val: number) => new Intl.NumberFormat('ja-JP').format(val);

    // Styles
    const baseInputStyle: React.CSSProperties = {
        textAlign: 'right',
        width: '100px',
        padding: '4px',
        fontSize: '13px',
        fontFamily: "'Roboto Mono', monospace",
        border: '1px solid #ddd',
        borderRadius: '2px',
    };

    const disabledInputStyle: React.CSSProperties = {
        ...baseInputStyle,
        backgroundColor: '#f5f5f5',
        color: '#888',
        border: '1px solid #eee',
    };

    // Table Render
    const renderTable = () => (
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #999', fontSize: '14px' }}>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0', zIndex: 5, boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>
                <tr>
                    <th style={{ padding: '8px', border: '1px solid #999', backgroundColor: '#e0e0e0', width: '35%', textAlign: 'center' }}>科目</th>
                    {isRevision ? (
                        <>
                            <th style={{ padding: '8px', border: '1px solid #999', backgroundColor: '#e3f2fd', width: '15%', textAlign: 'center' }}>前回予算額</th>
                            <th style={{ padding: '8px', border: '1px solid #999', backgroundColor: '#fff9c4', width: '15%', textAlign: 'center' }}>補正額</th>
                            <th style={{ padding: '8px', border: '1px solid #999', backgroundColor: '#e8f5e9', width: '15%', textAlign: 'center' }}>補正後予算額</th>
                        </>
                    ) : (
                        <>
                            <th style={{ padding: '8px', border: '1px solid #999', backgroundColor: currentTab === 1 ? '#f5f5f5' : '#ffffff', width: '20%', textAlign: 'center' }}>予算額</th>
                            <th style={{ padding: '8px', border: '1px solid #999', backgroundColor: currentTab === 1 ? '#e8f5e9' : '#ffffff', width: '20%', textAlign: 'center' }}>実績額</th>
                            <th style={{ padding: '8px', border: '1px solid #999', backgroundColor: currentTab === 1 ? '#fff3e0' : '#ffffff', width: '15%', textAlign: 'center' }}>差異</th>
                        </>
                    )}
                    <th style={{ padding: '8px', border: '1px solid #999', backgroundColor: '#e0e0e0', width: '8%', textAlign: 'center' }}>元帳</th>
                </tr>
            </thead>
            <tbody>
                {budgets.length === 0 && !loading ? (
                    <tr>
                        <td colSpan={isRevision ? 5 : 5} style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
                            大区分が登録されていません。勘定科目マスタで大区分・中区分を設定するか、旧システムからデータ移行を行ってください。
                        </td>
                    </tr>
                ) : null}
                {budgets.map((daikubun, dIdx) => (
                    <React.Fragment key={`d-${daikubun.id}`}>
                        {/* Daikubun Header Row */}
                        <tr style={{ backgroundColor: '#d0d0d0', fontWeight: 'bold' }}>
                            <td style={{ padding: '8px', border: '1px solid #999' }}>
                                {daikubun.code ? <span style={{ marginRight: '8px', display: 'inline-block', width: '30px' }}>{daikubun.code}</span> : null}
                                {daikubun.name}
                            </td>
                            <td colSpan={isRevision ? 3 : 3} style={{ padding: '8px', border: '1px solid #999', textAlign: 'right' }}>
                                {formatCurrency(daikubun.budget)}
                            </td>
                            <td style={{ border: '1px solid #999' }}></td>
                        </tr>

                        {daikubun.children.map((chukubun, cIdx) => (
                            <React.Fragment key={`c-${chukubun.id}`}>
                                {/* Chukubun Header Row */}
                                <tr style={{ backgroundColor: '#e8e8e8', fontWeight: 'bold' }}>
                                    <td style={{ padding: '8px 8px 8px 16px', border: '1px solid #999' }}>
                                        {chukubun.code ? <span style={{ marginRight: '8px', display: 'inline-block', width: '40px' }}>{chukubun.code}</span> : null}
                                        {chukubun.name}
                                    </td>
                                    <td colSpan={isRevision ? 3 : 3} style={{ padding: '8px', border: '1px solid #999', textAlign: 'right' }}>
                                        {formatCurrency(chukubun.budget)}
                                    </td>
                                    <td style={{ border: '1px solid #999' }}></td>
                                </tr>

                                {chukubun.children.map((kamoku, kIdx) => (
                                    <React.Fragment key={`k-${kamoku.id}`}>
                                        <tr style={{ backgroundColor: '#fff' }}>
                                            <td style={{ padding: '4px 4px 4px 32px', border: '1px solid #999', verticalAlign: 'middle' }}>
                                                {kamoku.code ? <span style={{ marginRight: '8px', display: 'inline-block', width: '50px', fontFamily: 'monospace' }}>{kamoku.code}</span> : null}
                                                {kamoku.name}
                                                {kamoku.children.length > 0 && (kamoku.kamoku_level_amount || 0) !== 0 ? (
                                                    <div style={{ marginTop: 2, marginLeft: kamoku.code ? 58 : 0, fontSize: 11, color: '#b71c1c' }}>
                                                        中区分に直接入っている額 {formatNumber(kamoku.kamoku_level_amount || 0)}円
                                                        （合計に含む・編集不可）
                                                    </div>
                                                ) : null}
                                            </td>

                                            {isRevision ? (
                                                <>
                                                    <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'right' }}>
                                                        {formatNumber(kamoku.initial_amount)}
                                                    </td>
                                                    <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'center' }}>
                                                        {kamoku.is_input ? (
                                                            <input
                                                                type="number"
                                                                value={kamoku.correction_amount}
                                                                onChange={(e) => handleBudgetChange(dIdx, cIdx, kIdx, null, e.target.value, true)}
                                                                style={{ ...baseInputStyle, backgroundColor: '#fffde7', width: '90%' }}
                                                                placeholder="0"
                                                            />
                                                        ) : (
                                                            <span style={{ color: '#888' }}>{formatNumber(kamoku.correction_amount)}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'right', fontWeight: 'bold' }}>
                                                        {formatNumber(kamoku.budget)}
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'center', backgroundColor: currentTab === 1 ? '#fcfcfc' : 'inherit' }}>
                                                        {kamoku.is_input ? (
                                                            <input
                                                                type="number"
                                                                value={kamoku.budget}
                                                                onChange={(e) => handleBudgetChange(dIdx, cIdx, kIdx, null, e.target.value)}
                                                                style={{ ...baseInputStyle, width: '90%' }}
                                                                placeholder="0"
                                                            />
                                                        ) : (
                                                            <span style={{ fontWeight: kamoku.children.length > 0 ? 'bold' : 'normal' }}>
                                                                {formatNumber(kamoku.budget)}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'right', backgroundColor: currentTab === 1 ? '#f1f8e9' : 'inherit', fontWeight: currentTab === 1 ? 'bold' : 'normal' }}>
                                                        {formatNumber(kamoku.actual)}
                                                    </td>
                                                    <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'right', backgroundColor: currentTab === 1 ? '#fff8e1' : 'inherit', color: kamoku.diff < 0 ? 'red' : 'inherit', fontWeight: currentTab === 1 ? 'bold' : 'normal' }}>
                                                        {formatNumber(kamoku.diff)}
                                                    </td>
                                                </>
                                            )}

                                            <td style={{ padding: '2px', border: '1px solid #999', textAlign: 'center' }}>
                                                <button
                                                    onClick={() => handleOpenLedger({ id: kamoku.id, name: kamoku.name })}
                                                    className={`ledger-btn ${ledgerState.kamokuId === kamoku.id && !ledgerState.hojoId && ledgerState.isOpen ? 'active' : ''}`}
                                                    title="元帳表示"
                                                    style={{ padding: '2px 4px', fontSize: '12px' }}
                                                >
                                                    元帳
                                                </button>
                                            </td>
                                        </tr>

                                        {kamoku.children.map((hojo, hIdx) => (
                                            <tr key={`h-${hojo.id}`} style={{ backgroundColor: '#fafafa' }}>
                                                <td style={{ padding: '4px 4px 4px 60px', border: '1px solid #999', fontSize: '13px', color: '#555' }}>
                                                    {hojo.code ? <span style={{ marginRight: '8px', display: 'inline-block', width: '40px', fontFamily: 'monospace' }}>{hojo.code}</span> : '└ '}
                                                    {hojo.name}
                                                </td>

                                                {isRevision ? (
                                                    <>
                                                        <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'right', fontSize: '13px' }}>
                                                            {formatNumber(hojo.initial_amount)}
                                                        </td>
                                                        <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'center' }}>
                                                            <input
                                                                type="number"
                                                                value={hojo.correction_amount}
                                                                onChange={(e) => handleBudgetChange(dIdx, cIdx, kIdx, hIdx, e.target.value, true)}
                                                                style={{ ...baseInputStyle, backgroundColor: '#fffde7', width: '90%' }}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'right', fontWeight: 'bold' }}>
                                                            {formatNumber(hojo.budget)}
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'center', backgroundColor: currentTab === 1 ? '#fcfcfc' : 'inherit' }}>
                                                            <input
                                                                type="number"
                                                                value={hojo.budget}
                                                                onChange={(e) => handleBudgetChange(dIdx, cIdx, kIdx, hIdx, e.target.value)}
                                                                style={{ ...baseInputStyle, width: '90%' }}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'right', fontSize: '13px', backgroundColor: currentTab === 1 ? '#f1f8e9' : 'inherit', fontWeight: currentTab === 1 ? 'bold' : 'normal' }}>
                                                            {formatNumber(hojo.actual)}
                                                        </td>
                                                        <td style={{ padding: '4px', border: '1px solid #999', textAlign: 'right', fontSize: '13px', backgroundColor: currentTab === 1 ? '#fff8e1' : 'inherit', color: hojo.diff < 0 ? 'red' : 'inherit', fontWeight: currentTab === 1 ? 'bold' : 'normal' }}>
                                                            {formatNumber(hojo.diff)}
                                                        </td>
                                                    </>
                                                )}

                                                <td style={{ padding: '2px', border: '1px solid #999', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => handleOpenLedger({ id: kamoku.id, name: kamoku.name }, { id: hojo.id, name: hojo.name })}
                                                        className={`ledger-btn ${ledgerState.hojoId === hojo.id && ledgerState.isOpen ? 'active' : ''}`}
                                                        title="補助元帳表示"
                                                        style={{ padding: '2px 4px', fontSize: '12px' }}
                                                    >
                                                        元帳
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}
            </tbody>
        </table>
    );

    // -------------------------------------------------------------------------
    // Main Layout
    // -------------------------------------------------------------------------

    return (
        <div style={{
            padding: '0 20px 20px 20px',
            fontFamily: "'Segoe UI', 'Meiryo', sans-serif",
            height: 'calc(100vh - 140px)', // Fixed height to fit in viewport (header + padding approx)
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden' // Prevent window scrolling
        }}>
            <style>{`
                .ledger-btn {
                    background: none;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    cursor: pointer;
                    padding: 4px 8px;
                    transition: all 0.2s;
                }
                .ledger-btn:hover {
                    background-color: #eee;
                }
                .ledger-btn.active {
                    background-color: #e3f2fd;
                    border-color: #2196f3;
                }
            `}</style>

            {/* Header / Controls */}
            {/* Header / Controls */}
            <div style={{ backgroundColor: '#fff', padding: '15px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '8px', border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
                    <button 
                        onClick={() => setCurrentTab(0)} 
                        style={{ padding: '6px 16px', border: 'none', cursor: 'pointer', backgroundColor: currentTab === 0 ? '#e3f2fd' : '#f5f5f5', fontWeight: currentTab === 0 ? 'bold' : 'normal', color: currentTab === 0 ? '#1976d2' : '#666' }}
                    >予算管理</button>
                    <button 
                        onClick={() => setCurrentTab(1)} 
                        style={{ padding: '6px 16px', border: 'none', cursor: 'pointer', backgroundColor: currentTab === 1 ? '#e8f5e9' : '#f5f5f5', fontWeight: currentTab === 1 ? 'bold' : 'normal', color: currentTab === 1 ? '#2e7d32' : '#666' }}
                    >実績対比</button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 'bold' }}>会計年度:</span>
                    <input type="number" value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value))} style={{ width: '60px', padding: '5px' }} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 'bold' }}>予算バージョン:</span>
                    <select
                        value={selectedVersionId || ''}
                        onChange={(e) => setSelectedVersionId(parseInt(e.target.value) || null)}
                        style={{ padding: '5px', minWidth: '200px' }}
                    >
                        {versions.map(v => (
                            <option key={v.id} value={v.id}>
                                {v.version_name} ({v.version_type === 'initial' ? '当初' : `補正 第${v.version_type.split('_')[1] || '?'}回`})
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => setShowNewVersionModal(true)} style={{ padding: '6px 12px', cursor: 'pointer' }}>＋ 新規バージョン</button>
                    {!isRevision && (
                        <select
                            onChange={(e) => { if (e.target.value) handleCopyFromPrevious(parseInt(e.target.value)); e.target.value = ''; }}
                            style={{ padding: '6px' }}
                        >
                            <option value="">前年度取込...</option>
                            {versions.filter(v => v.fiscal_year === fiscalYear - 1).map(v => (
                                <option key={v.id} value={v.id}>{v.fiscal_year}年度 {v.version_name}</option>
                            ))}
                        </select>
                    )}
                    {isRevision && (
                        <button onClick={handleFillFromActual} style={{ padding: '6px 12px', cursor: 'pointer' }}>⚡ 実績から作成</button>
                    )}
                </div>

                <div style={{ flex: 1 }} />

                <button
                    type="button"
                    onClick={handleDownloadMonthlyPdf}
                    disabled={loading || downloadingMonthlyPdf || !currentTenantId}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        opacity: loading || downloadingMonthlyPdf ? 0.7 : 1,
                    }}
                >
                    {downloadingMonthlyPdf ? 'PDF出力中...' : '月別執行PDF'}
                </button>

                <button
                    onClick={handleSaveBudgets}
                    disabled={loading || !selectedVersionId}
                    style={{
                        padding: '8px 24px',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        opacity: loading ? 0.7 : 1
                    }}>
                    保存
                </button>
            </div>

            {/* Notifications */}
            {error && <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>{error}</div>}
            {successMessage && <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>{successMessage}</div>}

            {/* Loading */}
            {loading && <div style={{ padding: '20px', textAlign: 'center' }}>読み込み中...</div>}

            {!loading && currentTenantId && versions.length === 0 && (
                <div style={{
                    marginBottom: '16px',
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#fff8e1',
                    border: '1px solid #ffcc80',
                    borderRadius: '4px',
                    color: '#5d4037',
                }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>この会計年度にはまだ予算バージョンがありません</p>
                    <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>
                        Web版では、入力の前に<strong>「＋ 新規バージョン」</strong>で当初予算（または補正）を作成してください。バージョンができると、下に入力用の表が表示されます。
                    </p>
                    <button
                        type="button"
                        onClick={() => setShowNewVersionModal(true)}
                        style={{ padding: '10px 20px', cursor: 'pointer', backgroundColor: '#ff9800', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold' }}
                    >
                        ＋ 新規バージョンを作成
                    </button>
                </div>
            )}

            {/* Split View Container（バージョン選択後は表を表示。行が0件でもメッセージ行を出す） */}
            {!loading && selectedVersionId && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: ledgerState.isOpen ? 'minmax(0, 1fr) 450px' : '1fr',
                    gap: '20px',
                    height: '100%',
                    flex: 1
                }}>
                    {/* Left: Table */}
                    <div style={{
                        overflowX: 'auto',
                        overflowY: 'auto',
                        border: '1px solid #999',
                        borderRadius: '0',
                        backgroundColor: '#fff',
                        height: 'calc(100% - 40px)' // Footer space
                    }}>
                        {renderTable()}
                    </div>

                    {/* Right: Ledger (Conditional) */}
                    {ledgerState.isOpen && (
                        <div style={{ border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <LedgerPanel
                                kamokuId={ledgerState.kamokuId}
                                kamokuName={ledgerState.kamokuName}
                                hojoId={ledgerState.hojoId}
                                hojoName={ledgerState.hojoName}
                                fiscalYear={fiscalYear}
                                apiFetch={apiFetch}
                                onClose={() => setLedgerState(prev => ({ ...prev, isOpen: false }))}
                                style={{
                                    position: 'relative',
                                    top: 0,
                                    right: 0,
                                    width: '100%',
                                    height: '100%',
                                    boxShadow: 'none',
                                    borderLeft: 'none'
                                }}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Footer Status Bar (Always Visible) */}
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#37474f', color: '#fff', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 -2px 10px rgba(0,0,0,0.2)', zIndex: 1000, gap: '30px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#cfd8dc' }}>資金収入計</span>
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#81c784' }}>{formatNumber(totals.income)}</span>
                </div>
                <div style={{ fontSize: '24px', color: '#b0bec5' }}>-</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#cfd8dc' }}>資金支出計</span>
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#e57373' }}>{formatNumber(totals.expense)}</span>
                </div>
                <div style={{ fontSize: '24px', color: '#b0bec5' }}>+</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#cfd8dc' }}>前年度繰越金 (手入力)</span>
                    <input
                        type="number"
                        value={carryoverAmount}
                        onChange={(e) => setCarryoverAmount(parseInt(e.target.value) || 0)}
                        style={{
                            fontSize: '16px',
                            textAlign: 'right',
                            width: '120px',
                            padding: '4px',
                            borderRadius: '4px',
                            border: '1px solid #546e7a',
                            backgroundColor: '#455a64',
                            color: '#fff',
                            fontWeight: 'bold'
                        }}
                    />
                </div>
                <div style={{ fontSize: '24px', color: '#b0bec5' }}>=</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#cfd8dc' }}>当期末支払資金残高</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px', fontWeight: 'bold', color: totals.finalBalance === 0 ? '#69f0ae' : '#ff5252' }}>
                            {formatNumber(totals.finalBalance)}
                        </span>
                        {totals.finalBalance === 0 ? (
                            <span style={{ color: '#69f0ae', fontSize: '12px', border: '1px solid #69f0ae', padding: '2px 6px', borderRadius: '10px' }}>OK</span>
                        ) : (
                            <span style={{ color: '#ff5252', fontSize: '12px', border: '1px solid #ff5252', padding: '2px 6px', borderRadius: '10px' }}>NG</span>
                        )}
                    </div>
                </div>
            </div>

            {/* New Version Modal */}
            {showNewVersionModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                    <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '8px', minWidth: '400px', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}>
                        <h3 style={{ margin: '0 0 20px 0', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>新規予算バージョン作成</h3>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>種別</label>
                            <select
                                value={newVersionType}
                                onChange={(e) => {
                                    setNewVersionType(e.target.value);
                                    if (e.target.value === 'initial') setNewVersionName('当初予算');
                                    else {
                                        const num = e.target.value.split('_')[1] || '1';
                                        setNewVersionName(`第${num}次補正予算`);
                                    }
                                }}
                                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                            >
                                <option value="initial">当初予算</option>
                                <option value="revision_1">第1次補正</option>
                                <option value="revision_2">第2次補正</option>
                                <option value="revision_3">第3次補正</option>
                                <option value="revision_4">第4次補正</option>
                            </select>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>表示名</label>
                            <input
                                type="text"
                                value={newVersionName}
                                onChange={(e) => setNewVersionName(e.target.value)}
                                style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button onClick={() => setShowNewVersionModal(false)} style={{ padding: '8px 16px', cursor: 'pointer', border: '1px solid #ccc', background: '#fff', borderRadius: '4px' }}>キャンセル</button>
                            <button onClick={handleCreateVersion} style={{ padding: '8px 16px', cursor: 'pointer', border: 'none', background: '#2196f3', color: '#fff', borderRadius: '4px', fontWeight: 'bold' }}>作成</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BudgetManagement;
